/**
 * Plain-language summary of a bulk retire where some items were refused.
 *
 * Items are never hard-deleted by users (owner ruling 2026-09-22) — "Delete"
 * became "Retire", which goes through the governed `retire_new_item` RPC and
 * can be refused when something already uses the item. Callers pass the real
 * counts plus the per-item reasons returned by `itemsStore.retireItems`, and
 * show this text, so a bulk action never announces "N retired" when some
 * were refused.
 */
export interface ItemRetireFailure {
  id: string;
  title?: string;
  reason: string;
  /** Set when the refusal is "already in use" rather than authority/state. */
  blockedBy?: string;
}

export function describeRetireFailures(
  retiredCount: number,
  requestedCount: number,
  failed: ItemRetireFailure[],
): string {
  const k = failed.length;
  if (k === 0) return `Retired ${retiredCount} of ${requestedCount}.`;
  const inUse = failed.every((f) => !!f.blockedBy) ? ' (already in use)' : '';
  const head = `Retired ${retiredCount} of ${requestedCount} — ${k} could not be retired${inUse}.`;

  // Group by reason so ten identical refusals read as one sentence.
  const byReason = new Map<string, ItemRetireFailure[]>();
  for (const f of failed) {
    const list = byReason.get(f.reason) ?? [];
    list.push(f);
    byReason.set(f.reason, list);
  }
  const parts = Array.from(byReason.entries()).map(([reason, list]) => {
    const names = list
      .slice(0, 3)
      .map((f) => (f.title?.trim() ? `“${f.title.trim()}”` : 'Untitled item'))
      .join(', ');
    const more = list.length > 3 ? ` and ${list.length - 3} more` : '';
    return `${names}${more}: ${reason}`;
  });
  return `${head} ${parts.join(' · ')}`;
}
